import { GetTimesheetsResponse, toWeeklyBreakdownByTimeType } from '@sit/client-shared';
import { getStartOfWeek, getStringFromDate } from '@sit/core';
import { usePostTimesheetSubmitAsync } from '@web/api/timesheet/use-post-timesheet-submit';
import { useShowOvertime } from '@web/api/users/use-show-overtime';
import { useEmployeeOvertimeCalculations } from '@web/hooks/useOvertimeEmployeeCalculations';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type MinimalTimesheetForSubmit = Pick<GetTimesheetsResponse[number], 'id' | 'employeeId' | 'startDate' | 'weekStart' | 'companyId'>;

export const useOvertimeSubmitGuard = () => {
  const { mutate: submitTimesheetAsync } = usePostTimesheetSubmitAsync();
  const [pendingSubmit, setPendingSubmit] = useState<MinimalTimesheetForSubmit | null>(null);
  const [showOvertimeWarning, setShowOvertimeWarning] = useState(false);

  const weekStartDate = useMemo(
    () => (pendingSubmit ? getStartOfWeek<string>(pendingSubmit.weekStart, getStringFromDate(pendingSubmit.startDate)) : undefined),
    [pendingSubmit],
  );

  const showOvertime = useShowOvertime({ timesheetId: pendingSubmit?.id });
  const { data: overtimeCalculations } = useEmployeeOvertimeCalculations({
    employeeId: pendingSubmit?.employeeId,
    weekStartDate,
    companyId: pendingSubmit?.companyId,
    timesheetId: pendingSubmit?.id,
  });

  const handleSubmitConfirm = useCallback(
    (overtimeComment?: string) => {
      if (!pendingSubmit) return;
      submitTimesheetAsync({
        pathParams: { id: pendingSubmit.id },
        body: {
          employeeOvertimeComment: overtimeComment || null,
        },
      });
      setShowOvertimeWarning(false);
      setPendingSubmit(null);
    },
    [pendingSubmit, submitTimesheetAsync],
  );

  const handleOvertimeWarningCancel = useCallback(() => {
    setShowOvertimeWarning(false);
    setPendingSubmit(null);
  }, []);

  useEffect(() => {
    if (!pendingSubmit) return;

    if (!showOvertime) {
      handleSubmitConfirm();
      return;
    }

    if (overtimeCalculations) {
      const { hasDiscrepancies } = toWeeklyBreakdownByTimeType(overtimeCalculations.overtimeBreakdown || {});
      if (hasDiscrepancies) {
        setShowOvertimeWarning(true);
      } else {
        handleSubmitConfirm();
      }
    }
  }, [pendingSubmit, showOvertime, overtimeCalculations, handleSubmitConfirm]);

  const requestSubmit = useCallback((timesheet: MinimalTimesheetForSubmit) => {
    setPendingSubmit(timesheet);
  }, []);

  const overtimeModal = {
    isOpen: showOvertimeWarning,
    overtimeCalculations,
    weekStartDate,
    onConfirm: handleSubmitConfirm,
    onCancel: handleOvertimeWarningCancel,
  } as const;

  return {
    requestSubmit,
    overtimeModal,
  } as const;
};
